/**
 * வாக்கிய பஞ்சாங்க இயந்திரம் — Vakya Panchangam Engine v2 (Browser ES Module)
 * 
 * EXACT PORT of generate_vishu_chithirai.js + suryan-engine.js + kali-suddha-dinam.js
 * from paranthaman5.0.  Pure computation — no I/O, no Node.js dependencies.
 * 
 * This version uses tharparai-level precision for moon calculations and
 * proper Boopathi interpolation for sun calculations — matching the working
 * generator that produced the cached app_data.js calendar.
 */

// ═══════════════════════════════════════════════════════════════
// CONSTANTS & NAMES
// ═══════════════════════════════════════════════════════════════

const THITHI_NAMES_EN = [
  'Prathamai', 'Dvitiyai', 'Tritiyai', 'Chaturthi', 'Panchami',
  'Shashti', 'Saptami', 'Ashtami', 'Navami', 'Dashami',
  'Ekadashi', 'Dwadashi', 'Trayodashi', 'Chaturdashi', 'Pournami',
  'Prathamai', 'Dvitiyai', 'Tritiyai', 'Chaturthi', 'Panchami',
  'Shashti', 'Saptami', 'Ashtami', 'Navami', 'Dashami',
  'Ekadashi', 'Dwadashi', 'Trayodashi', 'Chaturdashi', 'Amavasai'
];

const THITHI_NAMES_TA = [
  'ப்ரதமை', 'துவிதியை', 'த்ருதியை', 'சதுர்த்தி', 'பஞ்சமி',
  'ஷஷ்டி', 'ஸப்தமி', 'அஷ்டமி', 'நவமி', 'தசமி',
  'ஏகாதசி', 'துவாதசி', 'த்ரயோதசி', 'சதுர்தசி', 'பௌர்ணமி',
  'ப்ரதமை', 'துவிதியை', 'த்ருதியை', 'சதுர்த்தி', 'பஞ்சமி',
  'ஷஷ்டி', 'ஸப்தமி', 'அஷ்டமி', 'நவமி', 'தசமி',
  'ஏகாதசி', 'துவாதசி', 'த்ரயோதசி', 'சதுர்தசி', 'அமாவாசை'
];

const NAKSHATRA_NAMES_EN = [
  'Aswini', 'Bharani', 'Kiruthigai', 'Rohini', 'Mirugasirisham',
  'Thiruvathirai', 'Punarpoosam', 'Poosam', 'Ayilyam', 'Magam',
  'Pooram', 'Uthiram', 'Hastham', 'Chithirai', 'Swathi',
  'Visakam', 'Anusham', 'Kettai', 'Moolam', 'Pooradam',
  'Uthiradam', 'Thiruvonam', 'Avittam', 'Sathayam', 'Poorattaadhi',
  'Uthirattaadhi', 'Revathi'
];

const YOGA_NAMES_EN = [
  'Vishkumbha', 'Priti', 'Ayushman', 'Saubhagya', 'Shobhana',
  'Atiganda', 'Sukarman', 'Dhriti', 'Shula', 'Ganda',
  'Vriddhi', 'Dhruva', 'Vyaghata', 'Harshana', 'Vajram',
  'Siddhi', 'Vyatipatam', 'Variyan', 'Parigam', 'Shivam',
  'Siddha', 'Sadhya', 'Shubham', 'Shuklam', 'Brahma',
  'Mahendram', 'Vaidhriti'
];

const KARANA_NAMES = ['Bava', 'Balava', 'Kaulava', 'Taitula', 'Garaja', 'Vanija', 'Bhadra',
  'Sakuni', 'Chatushpada', 'Naga', 'Kimstughna'];

const MONTH_KEYS = ['chithirai', 'vaikasi', 'aani', 'aadi', 'aavani', 'purattasi',
  'aippasi', 'karthigai', 'margazhi', 'thai', 'masi', 'panguni'];

const MONTH_NAMES_EN = ['Chithirai', 'Vaikasi', 'Aani', 'Aadi', 'Aavani', 'Purattasi',
  'Aippasi', 'Karthigai', 'Margazhi', 'Thai', 'Masi', 'Panguni'];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Manyathi table (from generate_vishu_chithirai.js lines 54-68)
const MANYATHI_TABLE = [
  { name: 'panguni', base: 12, andharam: 3, totalDays: 30 },
  { name: 'chithirai', base: 15, andharam: -5, totalDays: 31 },
  { name: 'vaikasi', base: 10, andharam: -3, totalDays: 31 },
  { name: 'aani', base: 7, andharam: 1, totalDays: 32 },
  { name: 'aadi', base: 8, andharam: 3, totalDays: 31 },
  { name: 'aavani', base: 11, andharam: 5, totalDays: 31 },
  { name: 'purattasi', base: 16, andharam: 4, totalDays: 31 },
  { name: 'aippasi', base: 20, andharam: 1, totalDays: 30 },
  { name: 'karthigai', base: 21, andharam: -3, totalDays: 29 },
  { name: 'margazhi', base: 18, andharam: -5, totalDays: 29 },
  { name: 'thai', base: 13, andharam: -5, totalDays: 30 },
  { name: 'masi', base: 8, andharam: -2, totalDays: 30 },
  { name: 'panguni', base: 6, andharam: 2, totalDays: 30 }
];

// ═══════════════════════════════════════════════════════════════
// BASE-60 TIME MATH (exact port from suryan-engine.js)
// ═══════════════════════════════════════════════════════════════

function addTime(t1, t2) {
  let tharp = (t1.tharparai || 0) + (t2.tharparai || 0);
  let vin = (t1.vinadi || 0) + (t2.vinadi || 0) + Math.floor(tharp / 60);
  tharp = tharp % 60;
  let nazh = (t1.nazhigai || 0) + (t2.nazhigai || 0) + Math.floor(vin / 60);
  vin = vin % 60;
  let days = (t1.days || 0) + (t2.days || 0) + Math.floor(nazh / 60);
  nazh = nazh % 60;
  return { days, nazhigai: nazh, vinadi: vin, tharparai: tharp };
}

function subTime(t1, t2) {
  let th1 = t1.tharparai || 0, v1 = t1.vinadi || 0, n1 = t1.nazhigai || 0, d1 = t1.days || 0;
  let th2 = t2.tharparai || 0, v2 = t2.vinadi || 0, n2 = t2.nazhigai || 0, d2 = t2.days || 0;
  if (th1 < th2) { th1 += 60; v1 -= 1; }
  let tharp = th1 - th2;
  if (v1 < v2) { v1 += 60; n1 -= 1; }
  let vin = v1 - v2;
  if (n1 < n2) { n1 += 60; d1 -= 1; }
  let nazh = n1 - n2;
  let days = d1 - d2;
  return { days, nazhigai: nazh, vinadi: vin, tharparai: tharp };
}

// ═══════════════════════════════════════════════════════════════
// THARPARAI CONVERSION (exact from generate_vishu_chithirai.js)
// ═══════════════════════════════════════════════════════════════

function toTharparai(rasi, deg, min, sec, tharparai = 0) {
  return (rasi * 30 * 3600 * 60) + (deg * 3600 * 60) + (min * 60 * 60) + (sec * 60) + tharparai;
}

const DHRUVAM_0 = toTharparai(7, 2, 0, 7, 0);
const M1 = toTharparai(9, 27, 48, 10, 0);
const M2 = toTharparai(11, 7, 31, 1, 0);
const M3 = toTharparai(0, 27, 44, 6, 0);

// ═══════════════════════════════════════════════════════════════
// KALI SUDDHA DINAM (exact port from kali-suddha-dinam.js)
// ═══════════════════════════════════════════════════════════════

function calculateKaliSuddha(kaliYear) {
  const A = kaliYear;
  const step1 = A * 365;
  const quotientA4 = Math.floor(A / 4);
  const remainderA4 = A % 4;
  const step2 = step1 + quotientA4;
  const step3_nazhigai = remainderA4 * 15;
  const numerator = (A * 5) - 1237;
  const quotientStep4 = Math.floor(numerator / 576);
  const remainderStep4 = numerator % 576;
  let totalDays = step2 + quotientStep4;
  const valStep6 = remainderStep4 * 60;
  const quotientStep6 = Math.floor(valStep6 / 576);
  const remainderStep6 = valStep6 % 576;
  let totalNazhigai = step3_nazhigai + quotientStep6;
  const valStep7 = remainderStep6 * 60;
  const quotientStep7 = Math.floor(valStep7 / 576);
  const remainderStep7 = valStep7 % 576;
  let vinadi = quotientStep7;
  const valStep8 = remainderStep7 * 60;
  const quotientStep8 = Math.floor(valStep8 / 576);
  let tharparai = quotientStep8;
  if (totalNazhigai >= 60) {
    const extraDays = Math.floor(totalNazhigai / 60);
    totalDays += extraDays;
    totalNazhigai = totalNazhigai % 60;
  }
  return { days: totalDays, nazhigai: totalNazhigai, vinadi, tharparai };
}

// ═══════════════════════════════════════════════════════════════
// SURYAN ENGINE (exact port from suryan-engine.js)
// ═══════════════════════════════════════════════════════════════

function calculateSuryan(englishYear, monthKey, tamilDate, ahasTable, monthOffsets, boopathiTable) {
  const kaliYear = englishYear + 3101;
  const yearBirthRaw = calculateKaliSuddha(kaliYear);

  const monthOffset = monthOffsets[monthKey];
  const monthBirth = addTime(yearBirthRaw, monthOffset);

  const ahas = ahasTable[monthKey];
  let monthStartDays = monthBirth.days;
  if (monthBirth.nazhigai > ahas.nazhigai ||
    (monthBirth.nazhigai === ahas.nazhigai && monthBirth.vinadi >= ahas.vinadi)) {
    monthStartDays += 1;
  }

  const firstDateSunrise = { days: monthStartDays, nazhigai: 0, vinadi: 0, tharparai: 0 };
  const dateOffsetDays = tamilDate - 1;
  let finalKSD = addTime(firstDateSunrise, { days: dateOffsetDays });

  // Madhyama Sputam
  const madhyamaRaw = subTime(finalKSD, yearBirthRaw);
  const madhyama = {
    deg: madhyamaRaw.days % 360,
    min: madhyamaRaw.nazhigai,
    sec: madhyamaRaw.vinadi,
    third: madhyamaRaw.tharparai
  };

  // Boopathi interpolation
  const quotient = Math.floor(madhyama.deg / 10);
  const remainderDeg = madhyama.deg % 10;
  const b1 = quotient > 0 ? boopathiTable[String(quotient)] : 0;
  const b2 = boopathiTable[String(quotient + 1)] || b1;
  const diffBoopathi = b2 - b1;
  const remMinutes = (remainderDeg * 60) + madhyama.min + (madhyama.sec / 60) + (madhyama.third / 3600);
  const interpTotalMinutes = (diffBoopathi / 600) * remMinutes;
  const interpMin = Math.floor(interpTotalMinutes);
  const interpSecFloat = (interpTotalMinutes - interpMin) * 60;
  const interpSec = Math.floor(interpSecFloat);
  const interpThird = Math.round((interpSecFloat - interpSec) * 60);

  const totalBoopathi = addTime(
    { days: 0, nazhigai: b1, vinadi: 0, tharparai: 0 },
    { days: 0, nazhigai: interpMin, vinadi: interpSec, tharparai: interpThird }
  );

  const boopathiCorrection = {
    days: totalBoopathi.days,
    nazhigai: totalBoopathi.nazhigai,
    vinadi: totalBoopathi.vinadi,
    tharparai: totalBoopathi.tharparai
  };

  let suddhaSputam = subTime(
    { days: madhyama.deg, nazhigai: madhyama.min, vinadi: madhyama.sec, tharparai: madhyama.third },
    boopathiCorrection
  );
  if (suddhaSputam.days < 0) suddhaSputam.days += 360;

  // Thinagathi (Daily Motion)
  const thinagathiVigalai = diffBoopathi * 6;
  const thinagathiKalai = Math.floor(thinagathiVigalai / 60);
  const thinagathiRemVigalai = thinagathiVigalai % 60;

  let thinagathi;
  if (b2 > b1) {
    thinagathi = subTime(
      { days: 0, nazhigai: 60, vinadi: 0, tharparai: 0 },
      { days: 0, nazhigai: thinagathiKalai, vinadi: thinagathiRemVigalai, tharparai: 0 }
    );
  } else {
    thinagathi = addTime(
      { days: 0, nazhigai: 60, vinadi: 0, tharparai: 0 },
      { days: 0, nazhigai: thinagathiKalai, vinadi: thinagathiRemVigalai, tharparai: 0 }
    );
  }

  return {
    suddhaSputam,
    thinagathi: { kalai: thinagathi.nazhigai, vigalai: thinagathi.vinadi }
  };
}

// ═══════════════════════════════════════════════════════════════
// MOON CALCULATION (exact port from generate_vishu_chithirai.js)
// ═══════════════════════════════════════════════════════════════

function calculateMoonSunriseDynamic(englishYear, monthIndex, date, vakyamData, ahasTable, monthOffsets) {
  const kaliYear = englishYear + 3101;
  const ksd = calculateKaliSuddha(kaliYear);
  const yearBirthDays = ksd.days;
  const yearBirthN = ksd.nazhigai;
  const yearBirthV = ksd.vinadi;

  const monthKey = MONTH_KEYS[monthIndex];
  const monthData = monthOffsets[monthKey];

  const rawMonthDays = yearBirthDays + monthData.days;
  let rawMonthN = yearBirthN + monthData.nazhigai;
  let rawMonthV = yearBirthV + monthData.vinadi;
  if (rawMonthV >= 60) { rawMonthN++; rawMonthV -= 60; }

  const totalKSD = rawMonthDays + (date - 1);
  const D_rem = totalKSD - 1600984;

  const Q1 = Math.floor(D_rem / 12372);
  const R1 = D_rem % 12372;
  const P1 = Q1 * M1;

  const Q2 = Math.floor(R1 / 3031);
  const R2 = R1 % 3031;
  const P2 = Q2 * M2;

  const Q3 = Math.floor(R2 / 248);
  const R3 = R2 % 248;
  const P3 = Q3 * M3;

  const totalDhruvamT = DHRUVAM_0 + P1 + P2 + P3;
  const chandraVakyam = R3 === 0 ? 248 : R3;
  const vakyamRow = vakyamData.find(v => v.Vakyam === chandraVakyam);
  const chandraDuruvamKalais = vakyamRow['Chandra Duruvam'];

  const rawVakyamSputamT = totalDhruvamT + (chandraDuruvamKalais * 3600);

  const nextChandraVakyam = chandraVakyam === 248 ? 1 : chandraVakyam + 1;
  const nextVakyamRow = vakyamData.find(v => v.Vakyam === nextChandraVakyam);
  const chandraGadhiKalais = nextVakyamRow['Chandra Gadhi'];

  // Samskaram correction
  const samskaramSum = (Q2 * -8) + (Q3 * 32);
  const thinagathiDiff = chandraGadhiKalais - 791;
  const correctionTharparai = thinagathiDiff * samskaramSum;
  const correctedSputamT = rawVakyamSputamT + correctionTharparai;

  // Manyathi correction
  const prevMonthData = MANYATHI_TABLE[monthIndex];
  const currentMonthData = MANYATHI_TABLE[monthIndex + 1];
  const baseKalais = prevMonthData.base;
  const interpolatedVigalais = (currentMonthData.andharam * 60 / currentMonthData.totalDays) * date;
  const manyathiTotalTharparai = (baseKalais * 3600) + Math.round(interpolatedVigalais * 60);

  const finalSputamT = correctedSputamT + manyathiTotalTharparai;
  return {
    sputamTharparai: finalSputamT,
    thinagathiKalais: chandraGadhiKalais
  };
}

// ═══════════════════════════════════════════════════════════════
// PANCHANGAM CALCULATION (exact from generate_vishu_chithirai.js)
// ═══════════════════════════════════════════════════════════════

function getKaranaIndex(thithiIdx, isSecondHalf) {
  if (thithiIdx === 0 && !isSecondHalf) return 10; // Kimstughna
  if (thithiIdx === 29 && !isSecondHalf) return 8; // Chatushpada
  if (thithiIdx === 29 && isSecondHalf) return 9;  // Naga
  if (thithiIdx === 28 && isSecondHalf) return 7;  // Sakuni
  let kOffset = ((thithiIdx * 2) + (isSecondHalf ? 1 : 0)) - 1;
  return kOffset % 7;
}

function calcEndNazhigai(currentKalai, cycleKalais, gadhiSpeed) {
  let kalaisToPass = currentKalai % cycleKalais;
  let remainingKalais = cycleKalais - kalaisToPass;
  let forwardNazhigai = (remainingKalais / gadhiSpeed) * 60;
  if (forwardNazhigai > 60) forwardNazhigai = 60;
  let n = Math.floor(forwardNazhigai);
  let v = Math.round((forwardNazhigai - n) * 60);
  if (v === 60) { n += 1; v = 0; }
  return { n: String(n), v: String(v), float: forwardNazhigai };
}

function calculateDailyPanchangam(englishYear, monthIdx, date, vakyamData, ahasTable, monthOffsets, boopathiTable) {
  const monthKey = MONTH_KEYS[monthIdx];

  const moonResToday = calculateMoonSunriseDynamic(englishYear, monthIdx, date, vakyamData, ahasTable, monthOffsets);
  const moonKalaiToday = moonResToday.sputamTharparai / 3600;
  const sunResToday = calculateSuryan(englishYear, monthKey, date, ahasTable, monthOffsets, boopathiTable);
  const sunKalaiToday = (sunResToday.suddhaSputam.days * 60) +
    sunResToday.suddhaSputam.nazhigai +
    (sunResToday.suddhaSputam.vinadi / 60);

  const moonGadhi = moonResToday.thinagathiKalais;
  const sunGadhi = sunResToday.thinagathi.kalai + (sunResToday.thinagathi.vigalai / 60);

  // Thithi
  let thithiKalaiToday = moonKalaiToday - sunKalaiToday;
  if (thithiKalaiToday < 0) thithiKalaiToday += 21600;
  thithiKalaiToday %= 21600;
  const thithiIdx = Math.floor(thithiKalaiToday / 720);
  const thithiGadhi = moonGadhi - sunGadhi;
  const thithiEnd = calcEndNazhigai(thithiKalaiToday, 720, thithiGadhi);

  // Check for second thithi
  let t2Name = '', t2NameTa = '', t2n = '', t2v = '', t2idx = -1;
  if (thithiEnd.float < 60) {
    const nextThithiIdx = (thithiIdx + 1) % 30;
    const nextThithiRem = 720 - ((thithiKalaiToday + thithiGadhi * (thithiEnd.float / 60)) % 720);
    const nextThithiEnd = (nextThithiRem / thithiGadhi) * 60 + thithiEnd.float;
    if (nextThithiEnd < 60) {
      t2idx = nextThithiIdx;
      t2Name = THITHI_NAMES_EN[nextThithiIdx];
      t2NameTa = THITHI_NAMES_TA[nextThithiIdx];
      let n2 = Math.floor(nextThithiEnd);
      let v2 = Math.round((nextThithiEnd - n2) * 60);
      if (v2 === 60) { n2++; v2 = 0; }
      t2n = String(n2);
      t2v = String(v2);
    }
  }

  // Nakshatra
  let moonSputamOnly = moonKalaiToday % 21600;
  if (moonSputamOnly < 0) moonSputamOnly += 21600;
  const nakIdx = Math.floor(moonSputamOnly / 800);
  const nakEnd = calcEndNazhigai(moonSputamOnly, 800, moonGadhi);

  // Check for second nakshatra
  let n2Name = '', n2n = '', n2v = '';
  if (nakEnd.float < 60) {
    const nextNakIdx = (nakIdx + 1) % 27;
    const nextNakRem = 800 - ((moonSputamOnly + moonGadhi * (nakEnd.float / 60)) % 800);
    const nextNakEnd = (nextNakRem / moonGadhi) * 60 + nakEnd.float;
    if (nextNakEnd < 60) {
      n2Name = NAKSHATRA_NAMES_EN[nextNakIdx];
      let nn = Math.floor(nextNakEnd);
      let nv = Math.round((nextNakEnd - nn) * 60);
      if (nv === 60) { nn++; nv = 0; }
      n2n = String(nn);
      n2v = String(nv);
    }
  }

  // Yoga
  let yogaKalai = (moonKalaiToday + sunKalaiToday) % 21600;
  const yogaIdx = Math.floor(yogaKalai / 800);
  const yogaGadhi = moonGadhi + sunGadhi;
  const yogaEnd = calcEndNazhigai(yogaKalai, 800, yogaGadhi);

  // Check for second yoga
  let y2Name = '', y2n = '', y2v = '';
  if (yogaEnd.float < 60) {
    const nextYogaIdx = (yogaIdx + 1) % 27;
    const nextYogaRem = 800 - ((yogaKalai + yogaGadhi * (yogaEnd.float / 60)) % 800);
    const nextYogaEnd = (nextYogaRem / yogaGadhi) * 60 + yogaEnd.float;
    if (nextYogaEnd < 60) {
      y2Name = YOGA_NAMES_EN[nextYogaIdx];
      let yn = Math.floor(nextYogaEnd);
      let yv = Math.round((nextYogaEnd - yn) * 60);
      if (yv === 60) { yn++; yv = 0; }
      y2n = String(yn);
      y2v = String(yv);
    }
  }

  // Karana
  const karanamIdx = Math.floor(thithiKalaiToday / 360);
  const karanaArrIdx = getKaranaIndex(thithiIdx, karanamIdx % 2 === 1);
  const karanaEnd = calcEndNazhigai(thithiKalaiToday, 360, thithiGadhi);

  // Second karana
  let k2Name = '', k2n = '', k2v = '';
  if (karanaEnd.float < 60) {
    const nextKaraIdx = karanamIdx + 1;
    const nextKaranaArrIdx = getKaranaIndex(thithiIdx, nextKaraIdx % 2 === 1);
    const nextKaranaRem = 360 - ((thithiKalaiToday + thithiGadhi * (karanaEnd.float / 60)) % 360);
    const nextKaranaEnd = (nextKaranaRem / thithiGadhi) * 60 + karanaEnd.float;
    if (nextKaranaEnd < 60) {
      k2Name = KARANA_NAMES[nextKaranaArrIdx];
      let kn = Math.floor(nextKaranaEnd);
      let kv = Math.round((nextKaranaEnd - kn) * 60);
      if (kv === 60) { kn++; kv = 0; }
      k2n = String(kn);
      k2v = String(kv);
    }
  }

  return {
    t1: THITHI_NAMES_EN[thithiIdx],
    t1ta: THITHI_NAMES_TA[thithiIdx],
    t1idx: thithiIdx,
    t1n: thithiEnd.n,
    t1v: thithiEnd.v,
    t2: t2Name,
    t2ta: t2NameTa,
    t2idx,
    t2n, t2v,
    n1: NAKSHATRA_NAMES_EN[nakIdx % 27],
    n1n: nakEnd.n,
    n1v: nakEnd.v,
    n2: n2Name,
    n2n, n2v,
    y1: YOGA_NAMES_EN[yogaIdx % 27],
    y1n: yogaEnd.n,
    y1v: yogaEnd.v,
    y2: y2Name,
    y2n, y2v,
    k1: KARANA_NAMES[karanaArrIdx],
    k1n: karanaEnd.n,
    k1v: karanaEnd.v,
    k2: k2Name,
    k2n, k2v,
  };
}

// ═══════════════════════════════════════════════════════════════
// MAIN ENGINE CLASS
// ═══════════════════════════════════════════════════════════════

export class VakyaPanchangamEngine {
  constructor(data) {
    // data must contain: vakyamData, ahasTable, monthOffsets, boopathiTable
    this.vakyamData = data.vakyamData;
    this.ahasTable = data.ahasTable;
    this.monthOffsets = data.monthOffsets;
    this.boopathiTable = data.boopathiTable;
    this.cache = {};
    this._yearsCached = new Set();
  }

  /**
   * Generate a full Tamil year of panchangam from English year start
   */
  generateYearMap(startYear) {
    if (this._yearsCached.has(startYear)) return;

    const kaliYear = startYear + 3101;
    const ksd = calculateKaliSuddha(kaliYear);

    // Calculate month lengths from ahas table
    const monthLengths = [];
    for (let mi = 0; mi < 12; mi++) {
      const mk = MONTH_KEYS[mi];
      const monthOffset = this.monthOffsets[mk];
      const monthBirth = addTime(ksd, monthOffset);
      const ahas = this.ahasTable[mk];
      let startDay = monthBirth.days;
      if (monthBirth.nazhigai > ahas.nazhigai ||
        (monthBirth.nazhigai === ahas.nazhigai && monthBirth.vinadi >= ahas.vinadi)) {
        startDay += 1;
      }

      let nextMk, nextOffset, nextBirth, nextAhas, nextStartDay;
      if (mi < 11) {
        nextMk = MONTH_KEYS[mi + 1];
        nextOffset = this.monthOffsets[nextMk];
        nextBirth = addTime(ksd, nextOffset);
        nextAhas = this.ahasTable[nextMk];
        nextStartDay = nextBirth.days;
        if (nextBirth.nazhigai > nextAhas.nazhigai ||
          (nextBirth.nazhigai === nextAhas.nazhigai && nextBirth.vinadi >= nextAhas.vinadi)) {
          nextStartDay += 1;
        }
      } else {
        // Panguni to next year's Chithirai
        const nextKsd = calculateKaliSuddha(kaliYear + 1);
        const chithiraiOffset = this.monthOffsets['chithirai'];
        nextBirth = addTime(nextKsd, chithiraiOffset);
        nextAhas = this.ahasTable['chithirai'];
        nextStartDay = nextBirth.days;
        if (nextBirth.nazhigai > nextAhas.nazhigai ||
          (nextBirth.nazhigai === nextAhas.nazhigai && nextBirth.vinadi >= nextAhas.vinadi)) {
          nextStartDay += 1;
        }
      }
      monthLengths.push(nextStartDay - startDay);
    }

    // Generate English date starting from April 14 of startYear
    let d = new Date(Date.UTC(startYear, 3, 14));

    for (let m = 0; m < 12; m++) {
      const daysInMonth = monthLengths[m];
      for (let day = 1; day <= daysInMonth; day++) {
        const y = d.getUTCFullYear();
        const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
        const dt = String(d.getUTCDate()).padStart(2, '0');
        const key = `${y}-${mo}-${dt}`;

        try {
          const p = calculateDailyPanchangam(
            startYear, m, day,
            this.vakyamData, this.ahasTable, this.monthOffsets, this.boopathiTable
          );

          this.cache[key] = {
            tamilMonth: MONTH_NAMES_EN[m],
            tamilDate: day,
            wday: DAY_NAMES[d.getUTCDay()],
            ...p
          };
        } catch (e) {
          // Skip calculation errors for edge days
        }

        d.setTime(d.getTime() + 86400000);
      }
    }

    this._yearsCached.add(startYear);
  }

  /**
   * Get panchangam for a specific English date (YYYY-MM-DD format)
   */
  getForDate(dateStr) {
    if (this.cache[dateStr]) return this.cache[dateStr];

    const queryDate = new Date(dateStr);
    let sy = queryDate.getFullYear();
    if (queryDate.getMonth() < 3 || (queryDate.getMonth() === 3 && queryDate.getDate() < 14)) {
      sy -= 1;
    }

    this.generateYearMap(sy);
    return this.cache[dateStr] || null;
  }

  /**
   * Get all panchangam entries for a specific Tamil month in a given English year range
   */
  getMonthData(tamilMonth, englishYear) {
    const sy1 = englishYear - 1;
    const sy2 = englishYear;
    if (!this._yearsCached.has(sy1)) this.generateYearMap(sy1);
    if (!this._yearsCached.has(sy2)) this.generateYearMap(sy2);

    const results = [];
    for (const [dateKey, data] of Object.entries(this.cache)) {
      if (data.tamilMonth === tamilMonth) {
        const d = new Date(dateKey);
        const yr = d.getFullYear();
        if (yr === englishYear || yr === englishYear - 1 || yr === englishYear + 1) {
          results.push({ englishDate: dateKey, ...data });
        }
      }
    }
    results.sort((a, b) => a.englishDate.localeCompare(b.englishDate));
    return results;
  }

  clearCache() {
    this.cache = {};
    this._yearsCached = new Set();
  }
}

// Utility exports
export const THITHI_EN_TO_TA = {};
THITHI_NAMES_EN.forEach((en, i) => {
  THITHI_EN_TO_TA[en] = THITHI_NAMES_TA[i];
});

export function getPaksham(thithiIndex) {
  if (thithiIndex >= 0 && thithiIndex <= 14) return { en: 'Sukla', ta: 'சுக்ல பக்ஷம்' };
  return { en: 'Krishna', ta: 'கிருஷ்ண பக்ஷம்' };
}

export function getThithiNumber(thithiIndex) {
  return thithiIndex + 1;
}

export const THITHI_NAMES = THITHI_NAMES_EN;
export const NAKSHATRA_NAMES = NAKSHATRA_NAMES_EN;
export const YOGA_NAMES = YOGA_NAMES_EN;
export const MONTH_NAMES = MONTH_NAMES_EN;
export { THITHI_NAMES_TA };
